import {filter, pull} from 'lodash';

import {isDevelopment} from '../../../libs/shared/helpers/src/environment/envHelpers';
import {hasPersistedStorageAsync} from '../../../src/helpers/storageHelpers';
import {ActivityVisualTypesEnum} from '../../../src/models/api/activityModel';
import {ConversationModel} from '../../../src/models/api/conversationModel';
import {ApiConversationContentDataGroup, DataGroupModel} from '../../../src/models/api/dataGroupModel';
import {registerOfflineCacheForFetching} from './offlineFetch';
import {createOfflineCache} from './offlineResources';

const maxEntriesDevelopment = 20;
const maxEntriesPersistedStorage = 2000;
const maxEntriesEphemeralStorage = 400;

const defaultCache = createOfflineCache(
  'conversationContents',
  ApiConversationContentDataGroup,
  (dg) => dg.updatedAt || 0,
  async () => {
    // Other than saving bandwidth, this cuts down on noise in the network tab.
    if (isDevelopment()) {
      return maxEntriesDevelopment;
    }

    const hasPersistedStorage = await hasPersistedStorageAsync();
    if (hasPersistedStorage) {
      return maxEntriesPersistedStorage;
    }

    return maxEntriesEphemeralStorage;
  },
);

const cache = {
  ...defaultCache,
  readIfLatestAsync: readByConversationIdAsync,
};

function setup() {
  registerOfflineCacheForFetching(cache);
}

function removeAsync(conversation: ConversationModel) {
  return cache.removeAsync(conversation.id);
}

async function readByConversationIdAsync(
  conversationId: number,
  getLatestTimestamp: () => number | undefined,
) {
  const result = await defaultCache.readIfLatestAsync(conversationId, getLatestTimestamp);

  if (!result) {
    return undefined;
  }

  // Require the cached data group to have timeline metadata.
  // TODO: Update runtime types to validate this for us.
  if (!result.timeline) {
    return undefined;
  }

  const tasks = pendingTasks.filter((t) => t.conversationId === conversationId);

  if (!tasks.length) {
    return result;
  }

  return tasks.reduce(transformDataGroupForTask, result);
}

function transformDataGroupForTask(dg: DataGroupModel, task: PendingOfflineTask) {
  switch (task.type) {
    case 'deleteMessage':
      return {
        ...dg,
        activities: filter(
          dg.activities,
          (a) => a.visualType !== ActivityVisualTypesEnum.MESSAGE || a.messageUid !== task.messageUid,
        ),
        messages: filter(dg.messages, (m) => m.uid !== task.messageUid),
      };
    case 'deleteActivity':
      return {
        ...dg,
        activities: filter(dg.activities, (a) => a.id !== task.activityId),
      };
    default:
      return dg;
  }
}

function readIfLatestAsync(conversationId: number, getLatestTimestamp: () => number | undefined) {
  return readByConversationIdAsync(conversationId, getLatestTimestamp);
}

export const offlineConversationContents = {
  setup,
  canAddTimestampAsync: cache.canAddTimestampAsync.bind(cache),
  removeAsync,
  readIfLatestAsync,
  readStatsAsync: cache.readStatsAsync.bind(cache),
  addPendingOfflineTask,
};

/*
 * Pending tasks.
 */

type PendingOfflineTask =
  | {type: 'deleteMessage'; conversationId: number; messageUid: string}
  | {type: 'deleteActivity'; conversationId: number; activityId: number};

const pendingTasks: PendingOfflineTask[] = [];

function addPendingOfflineTask(task: PendingOfflineTask) {
  pendingTasks.push(task);

  return function taskIsDone() {
    pull(pendingTasks, task);
  };
}
